import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export function useProjectLikeCounts(projectIds: string[]) {
  return useQuery({
    queryKey: ['project-like-counts', projectIds],
    queryFn: async () => {
      if (!projectIds.length) return {} as Record<string, number>;
      const { data, error } = await supabase
        .from('project_likes')
        .select('project_id')
        .in('project_id', projectIds);
      if (error) throw error;
      const counts: Record<string, number> = {};
      for (const id of projectIds) counts[id] = 0;
      for (const row of data || []) {
        counts[row.project_id] = (counts[row.project_id] || 0) + 1;
      }
      return counts;
    },
    enabled: projectIds.length > 0,
  });
}

export function useMyLikes(projectIds: string[]) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['my-project-likes', user?.id, projectIds],
    queryFn: async () => {
      if (!user || !projectIds.length) return new Set<string>();
      const { data, error } = await supabase
        .from('project_likes')
        .select('project_id')
        .eq('user_id', user.id)
        .in('project_id', projectIds);
      if (error) throw error;
      return new Set((data || []).map(r => r.project_id));
    },
    enabled: !!user && projectIds.length > 0,
  });
}

export function useToggleLike() {
  const { user } = useAuth();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ projectId, isLiked }: { projectId: string; isLiked: boolean }) => {
      if (!user) throw new Error('Not authenticated');
      if (isLiked) {
        const { error } = await supabase
          .from('project_likes')
          .delete()
          .eq('project_id', projectId)
          .eq('user_id', user.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('project_likes')
          .insert({ project_id: projectId, user_id: user.id });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['project-like-counts'] });
      qc.invalidateQueries({ queryKey: ['my-project-likes'] });
    },
  });
}
